export type StandardDelegationFieldConfig = {
    kind: "text" | "textarea" | "date" | "checkbox" | "checkboxGroup" | "radioGroup";
    field: string;
    label: string;
    cols?: string;
    rows?: number;
    options?: string[] | Array<{ label: string; value: string }>;
};

export type StandardDelegationSectionConfig = {
    title: string;
    subtitle: string;
    fields: StandardDelegationFieldConfig[];
};

export type StandardDelegationFormDefinition = {
    templateVersion?: string;
    schemaVersion?: number;
    notes?: string[];
    instructionsTitle?: string;
    instructions?: string[];
    printHint?: string;
    printHeader?: {
        bannerLines?: string[];
        title: string;
        revision: string;
        pageLabel?: string;
        subtitle?: string;
    };
    printFooter?: string[];
    defaults: Record<string, any>;
    sections: StandardDelegationSectionConfig[];
};

export type StandardDelegationTemplateSnapshot = StandardDelegationFormDefinition & {
    templateVersion: string;
    schemaVersion: number;
};

export const createEmptyPrnMedicationOrder = () => ({
    dateOrdered: "",
    medicationName: "",
    doseFrequency: "",
    route: "",
    notToExceed: "",
    reason: "",
    symptomsAmount: "",
    notes: "",
});

export const createEmptyRescindingDelegationRow = () => ({
    caregiverName: "",
    medicationOrTreatment: "",
    notes: "",
});

export const createEmptyLtcwCompetencyEntry = () => ({
    ltcwName: "",
    observationDemonstration: false,
    verbalDescription: false,
    recordReview: false,
    trainingNeededDate: "",
    trainingCompletedDate: "",
    otherSpecify: "",
    activeCredential: "yes",
});

export const STANDARD_DELEGATION_FORM_DEFINITIONS: Record<string, StandardDelegationFormDefinition> = {
    "06-200": {
        templateVersion: "06-200@2023-05",
        schemaVersion: 1,
        notes: [
            "DSHS lists this billing form as not mandatory but encouraged.",
            "The official billing sheet is a one-page monthly worksheet with repeated resident billing rows and a service code H2014 taxonomy footer.",
        ],
        instructionsTitle: "Official Billing Worksheet Guidance",
        instructions: [
            "This worksheet is used to summarize Registered Nurse delegation billing units for the billing month shown on the form.",
            "The official DSHS page identifies this form as encouraged, not mandatory, but it should still match the monthly billing worksheet structure when used.",
            "Enter the RN name, NPI number, billing month, resident name, date of birth, and ICD-10 code before recording service units.",
            "Record units in the same billing categories shown on the official sheet: Assessment / Training, Collateral Contact, Travel Time, Documentation, and Billing.",
            "Use the H2014 service-code taxonomy shown on the official form. The footer also notes that 1 unit equals 15 minutes.",
            "Keep billing notes specific enough to support the monthly total and any supporting documentation retained with the resident record.",
        ],
        printHint: "Print in a compact worksheet layout so service-unit rows stay grouped together.",
        printHeader: {
            title: "Registered Nurse (RN) Delegation Billing",
            revision: "DSHS 06-200 (05/2023)",
            subtitle: "Taxonomy: Service Code: H2014 1 Unit = 15 minutes",
        },
        printFooter: [
            "Taxonomy: Service Code: H2014",
            "1 Unit = 15 minutes",
        ],
        defaults: {
            rndName: "",
            npiNumber: "",
            billingMonth: "",
            taxonomyServiceCode: "H2014",
            clientName: "",
            clientDob: "",
            icd10Code: "",
            assessmentTrainingUnits: "",
            collateralContactUnits: "",
            travelTimeUnits: "",
            documentationUnits: "",
            billingUnits: "",
            totalUnits: "",
            billingNotes: "",
            submittedBy: "",
            submittedDate: "",
        },
        sections: [
            {
                title: "Billing Header",
                subtitle: "Track the billing month, billing nurse details, and the resident tied to this delegation billing entry.",
                fields: [
                    { kind: "text", field: "rndName", label: "Registered Nurse Delegator", cols: "col-12 md:col-6" },
                    { kind: "text", field: "npiNumber", label: "NPI Number", cols: "col-12 md:col-3" },
                    { kind: "text", field: "taxonomyServiceCode", label: "Taxonomy / Service Code", cols: "col-12 md:col-3" },
                    { kind: "text", field: "clientName", label: "Client Name", cols: "col-12 md:col-4" },
                    { kind: "date", field: "clientDob", label: "Client Date of Birth", cols: "col-12 md:col-4" },
                    { kind: "text", field: "icd10Code", label: "ICD-10 Code", cols: "col-12 md:col-4" },
                    { kind: "text", field: "billingMonth", label: "Billing Month", cols: "col-12 md:col-4" },
                ],
            },
            {
                title: "Service Units",
                subtitle: "Capture the billable work completed for assessment, collateral contact, travel, documentation, and total billing.",
                fields: [
                    { kind: "text", field: "assessmentTrainingUnits", label: "Assessment / Training", cols: "col-12 md:col-4" },
                    { kind: "text", field: "collateralContactUnits", label: "Collateral Contact", cols: "col-12 md:col-4" },
                    { kind: "text", field: "travelTimeUnits", label: "Travel Time", cols: "col-12 md:col-4" },
                    { kind: "text", field: "documentationUnits", label: "Documentation", cols: "col-12 md:col-4" },
                    { kind: "text", field: "billingUnits", label: "Billing", cols: "col-12 md:col-4" },
                    { kind: "text", field: "totalUnits", label: "Total Units", cols: "col-12 md:col-4" },
                    { kind: "textarea", field: "billingNotes", label: "Billing Notes", cols: "col-12", rows: 4 },
                ],
            },
            {
                title: "Submission",
                subtitle: "Record who prepared the billing entry and when it was finalized.",
                fields: [
                    { kind: "text", field: "submittedBy", label: "Prepared By", cols: "col-12 md:col-6" },
                    { kind: "date", field: "submittedDate", label: "Prepared Date", cols: "col-12 md:col-6" },
                ],
            },
        ],
    },
    "10-217": {
        templateVersion: "10-217@2026-05",
        schemaVersion: 1,
        notes: [
            "This template follows the DSHS 10-217 PDF revision dated May 2026.",
            "Attach the verification documents required by the official form.",
        ],
        instructionsTitle: "Instructions - Nurse Delegation: Credentials and Training Verification",
        instructions: [
            "1. Long Term Care Worker's (LTCW) Name: Enter name of Long Term Care Worker (print).",
            "Credential Verification: Identify credential and certification type (Nursing Assistant Registered (NAR), Nursing Assistant Certified (NAC), Home Care Aide Certified (HCA-C)), credential number and the expiration date of the credential.",
            "2. Required LTCW Nurse Delegation Training Verification: Verify the following nurse delegation training and attach copy of verification.",
            "The LTCW must complete the 9-hour Nurse Delegation Core Training for Nursing Assistants and Home Care Aides prior to delegation. Enter date of completion.",
            "If the LTCW will also be administering insulin, they must also complete the 3-hour Nurse Delegation Special Focus on Diabetes. Enter date of completion.",
            "3. Basic Training Verification for Nursing Assistant Registered (NAR): Verify which training the LTCW completed and the date of completion.",
            "4. Exempt Long-Term Care Workers: Verification letter must be attached with this document and date of RNDs verification.",
            "5. RND Signature: The delegating nurse must sign verifying they have observed approved training and credentials for the LTCW.",
            "6. Date of RND verification of training and credentials.",
        ],
        printHint: "Keep the printed layout close to the numbered DSHS verification checklist.",
        printHeader: {
            title: "Nurse Delegation: Credentials and Training Verification",
            revision: "DSHS 10-217 (Rev. 05/2026)",
            subtitle: "Follow the May 2026 DSHS field wording and section order.",
        },
        printFooter: [

        ],
        defaults: {
            ltcwName: "",
            credentialType: [] as string[],
            credentialNumber: "",
            credentialExpirationDate: "",
            trainingVerificationAttachment: [] as string[],
            nurseDelegationCoreTrainingDate: "",
            specialFocusOnDiabetesDate: "",
            basicTrainingCertificateDate: "",
            basicTraining75HoursDate: "",
            fundamentalsOfCareDate: "",
            revisedFundamentalsOfCareDate: "",
            dda32HourLetterDate: "",
            exemptEmploymentVerificationAttachment: [] as string[],
            exemptEmploymentVerificationDate: "",
            rnSignature: "",
            dateVerified: "",
        },
        sections: [
            {
                title: "1. LTCW Name and Active Credential Verification",
                subtitle: "Official section 1.",
                fields: [
                    { kind: "text", field: "ltcwName", label: "Long-Term Care Worker's (LTCWs) Name (print)", cols: "col-12" },
                    { kind: "checkboxGroup", field: "credentialType", label: "Provide the Washington State Credential Information", cols: "col-12", options: ["NAR", "NAC", "HCA-C"] },
                    { kind: "text", field: "credentialNumber", label: "Credential Number", cols: "col-12 md:col-8" },
                    { kind: "date", field: "credentialExpirationDate", label: "Expiration Date", cols: "col-12 md:col-4" },
                ],
            },
            {
                title: "2. Required LTCW Nurse Delegation Training Verification",
                subtitle: "Official section 2.",
                fields: [
                    { kind: "checkboxGroup", field: "trainingVerificationAttachment", label: "Must attach copy of verification", cols: "col-12", options: ["Copy of verification attached"] },
                    { kind: "date", field: "nurseDelegationCoreTrainingDate", label: "9-hour Nurse Delegation Core Training for Nursing Assistants Date", cols: "col-12 md:col-6" },
                    { kind: "date", field: "specialFocusOnDiabetesDate", label: "3-hour Nurse Delegation Special Focus on Diabetes for insulin administration Date", cols: "col-12 md:col-6" },
                ],
            },
            {
                title: "3. Basic Training Verification for Nursing Assistant Registered (NAR) before Delegation",
                subtitle: "Official section 3.",
                fields: [
                    { kind: "date", field: "basicTrainingCertificateDate", label: "Basic Training (Core Competency) Certificate or Transcript of Completion Date", cols: "col-12 md:col-6" },
                    { kind: "date", field: "basicTraining75HoursDate", label: "75 Hours of DSHS Approved HCA Basic Training certificate or transcript Date", cols: "col-12 md:col-6" },
                    { kind: "date", field: "fundamentalsOfCareDate", label: "Fundamentals of Care (FOC) Date", cols: "col-12 md:col-4" },
                    { kind: "date", field: "revisedFundamentalsOfCareDate", label: "Revised Fundamentals of Care (RFOC) Date", cols: "col-12 md:col-4" },
                    { kind: "date", field: "dda32HourLetterDate", label: "DDA 32 Hour Letter Date", cols: "col-12 md:col-4" },
                ],
            },
            {
                title: "4. Exempt Long-Term Care Workers",
                subtitle: "Official section 4.",
                fields: [
                    { kind: "checkboxGroup", field: "exemptEmploymentVerificationAttachment", label: "Letter of employment verification attached", cols: "col-12", options: ["Letter of employment verification attached"] },
                    { kind: "date", field: "exemptEmploymentVerificationDate", label: "Date of RND Verification", cols: "col-12 md:col-4" },
                ],
            },
            {
                title: "5. RN Delegator Verification",
                subtitle: "Official sections 5 and 6.",
                fields: [
                    { kind: "text", field: "rnSignature", label: "RN Printed Name", cols: "col-12 md:col-8" },
                    { kind: "date", field: "dateVerified", label: "Date Verified", cols: "col-12 md:col-4" },
                ],
            },
        ],
    },
    "13-678": {
        templateVersion: "13-678@current",
        schemaVersion: 1,
        notes: [
            "This form should match the official DSHS 13-678-2 layout and wording as closely as possible.",
            "Use one record for the currently delegated task instructions and keep it with the resident chart.",
        ],
        instructionsTitle: "Instructions for Completing Nurse Delegation: Instructions for Nursing Task",
        instructions: [
            "Complete each field using the same order shown on the official DSHS 13-678-2 template.",
            "Use the Date Task Delegated field for the date the task is first delegated.",
            "Write the delegated task and expected outcome clearly, then list medication details only if medications are being delegated.",
            "Use the verification fields to document the date, name/title, and method used to verify the delegated medication process.",
            "Use the reporting section to document what should be reported to the RND, the health care provider, and 911.",
            "If the Consumer Directed Employer section applies, include the Individual Provider name and ProviderOne number.",
        ],
        printHint: "Keep this form aligned to the official DSHS 13-678-2 template and field order.",
        printHeader: {
            title: "Nurse Delegation: Instructions for Nursing Task",
            revision: "DSHS 13-678-2",
            subtitle: "Use the latest official DSHS task instruction field order for delegation records.",
        },
        printFooter: [

        ],
        defaults: {
            clientName: "",
            acesId: "",
            clientDob: "",
            setting: "",
            dateTaskDelegated: "",
            delegatedTaskAndExpectedOutcome: "",
            delegatedMedicationAttachmentIncluded: false,
            delegatedMedicationDetails: "",
            verificationDate: "",
            verificationNameTitle: "",
            verificationMethod: "",
            stepsToPerformTask: "",
            additionalTeachingAidesAttached: false,
            additionalTeachingAides: "",
            rndName: "",
            rndPhone: "",
            reportToRnd: "",
            healthCareProvider: "",
            healthCareProviderPhone: "",
            reportToHealthCareProvider: "",
            reportToEmergencyServices: "",
            rndSignature: "",
            rndSignatureDate: "",
            individualProviderName: "",
            providerOneNumber: "",
        },
        sections: [
            {
                title: "Resident Details",
                subtitle: "Enter the resident identification details in the same order shown on the official template.",
                fields: [
                    { kind: "text", field: "clientName", label: "Client Name", cols: "col-12 md:col-4" },
                    { kind: "text", field: "acesId", label: "ACES Client ID Number", cols: "col-12 md:col-4" },
                    { kind: "date", field: "clientDob", label: "Date of Birth", cols: "col-12 md:col-4" },
                    { kind: "text", field: "setting", label: "ID / Setting (Optional)", cols: "col-12 md:col-6" },
                    { kind: "date", field: "dateTaskDelegated", label: "Date Task Delegated", cols: "col-12 md:col-6" },
                ],
            },
            {
                title: "Task Instructions",
                subtitle: "Document the delegated task, medication details, and the verification information from the template.",
                fields: [
                    { kind: "textarea", field: "delegatedTaskAndExpectedOutcome", label: "Delegated Task and Expected Outcome", cols: "col-12", rows: 4 },
                    { kind: "checkbox", field: "delegatedMedicationAttachmentIncluded", label: "Additional Medication Form Attached", cols: "col-12 md:col-4" },
                    { kind: "textarea", field: "delegatedMedicationDetails", label: "List Specific Medication(s), Dosages, and Frequency of Medications Delegated on This Date", cols: "col-12", rows: 4 },
                    { kind: "date", field: "verificationDate", label: "Verification Date", cols: "col-12 md:col-4" },
                    { kind: "text", field: "verificationNameTitle", label: "Verification Name / Title", cols: "col-12 md:col-4" },
                    { kind: "textarea", field: "verificationMethod", label: "Method of Verification", cols: "col-12 md:col-8", rows: 3 },
                    { kind: "textarea", field: "stepsToPerformTask", label: "Steps to Perform the Task", cols: "col-12", rows: 5 },
                    { kind: "checkbox", field: "additionalTeachingAidesAttached", label: "Additional Teaching Aides / Materials Attached", cols: "col-12 md:col-6" },
                    { kind: "text", field: "additionalTeachingAides", label: "Additional Teaching Aides / Materials Details", cols: "col-12 md:col-6" },
                ],
            },
            {
                title: "Reporting Contacts",
                subtitle: "List the reporting instructions and contact details exactly as needed on the template.",
                fields: [
                    { kind: "text", field: "rndName", label: "RND Name (Print)", cols: "col-12 md:col-6" },
                    { kind: "text", field: "rndPhone", label: "RND Telephone Number", cols: "col-12 md:col-6" },
                    { kind: "textarea", field: "reportToRnd", label: "What to Report to RND", cols: "col-12 md:col-6", rows: 4 },
                    { kind: "text", field: "healthCareProvider", label: "Health Care Provider Name", cols: "col-12 md:col-6" },
                    { kind: "text", field: "healthCareProviderPhone", label: "Health Care Provider Telephone Number", cols: "col-12 md:col-6" },
                    { kind: "textarea", field: "reportToHealthCareProvider", label: "What to Report to Health Care Provider", cols: "col-12 md:col-6", rows: 4 },
                    { kind: "textarea", field: "reportToEmergencyServices", label: "What to Report to 911", cols: "col-12", rows: 4 },
                ],
            },
            {
                title: "Signature and Consumer Directed Employer",
                subtitle: "Use the final template fields for the RND signature/date line and Consumer Directed Employer details.",
                fields: [
                    { kind: "text", field: "rndSignature", label: "RND Signature", cols: "col-12 md:col-6" },
                    { kind: "date", field: "rndSignatureDate", label: "Date", cols: "col-12 md:col-6" },
                    { kind: "text", field: "individualProviderName", label: "Individual Provider (IP) Name", cols: "col-12 md:col-6" },
                    { kind: "text", field: "providerOneNumber", label: "ProviderOne Number", cols: "col-12 md:col-6" },
                ],
            },
        ],
    },
    "13-678-1": {
        templateVersion: "13-678-1@current",
        schemaVersion: 1,
        notes: [
            "This form follows the DSHS 13-678-1 Consent for Delegation Process template.",
            "Resident, provider, and delegating RN details can be prefilled from the workspace context and reviewed before saving.",
        ],
        instructionsTitle: "Instructions for Completing Consent for Delegation Process",
        instructions: [
            "Review the resident, provider, and delegating RN information before saving.",
            "Complete the consent, contact, diagnosis, allergy, and setting fields required by the official form.",
            "The delegating RN name and verification date are prefilled from the signed-in RN when available.",
        ],
        printHint: "Keep the printed layout aligned with the official DSHS 13-678-1 consent form.",
        printHeader: {
            title: "Consent for Delegation Process",
            revision: "DSHS 13-678-1",
            subtitle: "Nurse delegation consent and RN verification.",
        },
        printFooter: [],
        defaults: {
            clientName: "",
            acesId: "",
            clientDob: "",
            clientAddress: "",
            clientCity: "",
            clientState: "WA",
            clientZipCode: "",
            clientPhone: "",
            clientDiagnosis: "",
            clientAllergies: "",
            setting: "AFH",
            facilityProgramContact: "",
            facilityPhone: "",
            facilityFax: "",
            facilityEmail: "",
            providerName: "",
            healthCareProvider: "",
            healthCareProviderPhone: "",
            clientSignature: "",
            clientSignaturePhone: "",
            clientSignatureDate: "",
            verbalConsentObtainedFrom: "",
            relationshipToClient: "",
            verbalConsentDate: "",
            delegatingRnName: "",
            delegatingRnPhone: "",
            delegatingRnSignature: "",
            delegatingRnDate: "",
        },
        sections: [
            {
                title: "Client and Provider Details",
                subtitle: "Review the resident, home/provider, health care provider, and contact information.",
                fields: [
                    { kind: "text", field: "clientName", label: "Client Name", cols: "col-12 md:col-6" },
                    { kind: "text", field: "acesId", label: "ACES Client ID Number", cols: "col-12 md:col-6" },
                    { kind: "date", field: "clientDob", label: "Date of Birth", cols: "col-12 md:col-4" },
                    { kind: "text", field: "clientAddress", label: "Client Address", cols: "col-12 md:col-8" },
                    { kind: "text", field: "clientCity", label: "City", cols: "col-12 md:col-4" },
                    { kind: "text", field: "clientState", label: "State", cols: "col-12 md:col-4" },
                    { kind: "text", field: "clientZipCode", label: "ZIP Code", cols: "col-12 md:col-4" },
                    { kind: "text", field: "clientPhone", label: "Client Telephone Number", cols: "col-12 md:col-6" },
                    { kind: "text", field: "clientDiagnosis", label: "Client Diagnosis", cols: "col-12 md:col-6" },
                    { kind: "text", field: "clientAllergies", label: "Client Allergies", cols: "col-12" },
                    { kind: "text", field: "providerName", label: "Provider", cols: "col-12 md:col-6" },
                    { kind: "text", field: "healthCareProvider", label: "Health Care Provider", cols: "col-12 md:col-6" },
                    { kind: "text", field: "healthCareProviderPhone", label: "Health Care Provider Telephone Number", cols: "col-12 md:col-6" },
                ],
            },
            {
                title: "Facility or Program Contact",
                subtitle: "Capture the contact details for the facility or program.",
                fields: [
                    { kind: "text", field: "facilityProgramContact", label: "Facility or Program Contact", cols: "col-12 md:col-6" },
                    { kind: "text", field: "facilityPhone", label: "Telephone Number", cols: "col-12 md:col-6" },
                    { kind: "text", field: "facilityFax", label: "Fax Number", cols: "col-12 md:col-6" },
                    { kind: "text", field: "facilityEmail", label: "Email Address", cols: "col-12 md:col-6" },
                    { kind: "radioGroup", field: "setting", label: "Setting", cols: "col-12", options: ["AFH", "DDA", "Assisted Living", "Private Home"] },
                ],
            },
            {
                title: "Consent for the Delegation Process",
                subtitle: "Record written or verbal consent and the relationship of the person giving consent.",
                fields: [
                    { kind: "text", field: "clientSignature", label: "Client or Authorized Representative Signature", cols: "col-12 md:col-6" },
                    { kind: "text", field: "clientSignaturePhone", label: "Telephone Number", cols: "col-12 md:col-6" },
                    { kind: "date", field: "clientSignatureDate", label: "Date", cols: "col-12 md:col-4" },
                    { kind: "text", field: "verbalConsentObtainedFrom", label: "Verbal Consent Obtained From", cols: "col-12 md:col-8" },
                    { kind: "text", field: "relationshipToClient", label: "Relationship to Client", cols: "col-12 md:col-6" },
                    { kind: "date", field: "verbalConsentDate", label: "Date", cols: "col-12 md:col-6" },
                ],
            },
            {
                title: "Delegating RN Verification",
                subtitle: "The signed-in Registered Nurse is prefilled as the delegating RN when profile details are available.",
                fields: [
                    { kind: "text", field: "delegatingRnName", label: "Delegating RN Name - Print", cols: "col-12 md:col-6" },
                    { kind: "text", field: "delegatingRnPhone", label: "Telephone Number", cols: "col-12 md:col-6" },
                    { kind: "text", field: "delegatingRnSignature", label: "Delegating RN Signature", cols: "col-12 md:col-6" },
                    { kind: "date", field: "delegatingRnDate", label: "Date", cols: "col-12 md:col-6" },
                ],
            },
        ],
    },
    "13-678A": {
        templateVersion: "13-678A@current",
        schemaVersion: 1,
        notes: [
            "To register concerns or complaints about Nurse Delegation, the official form lists 1-800-562-6078.",
            "Distribution on the official form: copy in client chart and in RND file.",
            "The instruction page says repeat boxes 5 through 14 for each additional PRN medication ordered at that time.",
        ],
        instructionsTitle: "Instructions for Completing Nurse Delegation: PRN Medication",
        instructions: [
            "All fields are required unless indicated \"OPTIONAL\".",
            "1. Client Name: Enter ND client's name (last name, first name).",
            "2. ACES ID Number: Enter client's ACES ID number.",
            "3. Date of Birth: Enter ND client's date of birth (month, day, year).",
            "4. Setting: Enter setting such as AFH, ALF, DDA program, or In-home.",
            "5. Date Ordered: Enter the date the PRN medication was ordered.",
            "6. Name of Medication: Enter the name of the medication ordered.",
            "7. Dose / Frequency: Enter dose and frequency of medication to be given.",
            "8. Route: Enter administration route such as PO, suppository, topical, drops, and similar routes.",
            "9. Not to Exceed: Enter the maximum number of doses in a specified time period, if applicable.",
            "10. Reason For Medication: Enter the action or reason the medication is given.",
            "11. Symptoms for Administration and Amount To Be Given: Enter the behavior or symptom the client will display when the medication is needed, plus the dose to give when that behavior or symptom is observed.",
            "12. Notes: Enter additional administration information such as side effects and expected outcome.",
            "13. and 14. RND Signature and Date: Sign and date legibly.",
            "Repeat boxes 5 through 14 for each additional PRN medication ordered at this time.",
        ],
        printHint: "Keep each PRN medication order block together on print so the medication instructions read like the original form.",
        printHeader: {
            bannerLines: [
                "DISTRIBUTION: Copy in client chart and in RND file",
            ],
            title: "Nurse Delegation: PRN Medication",
            revision: "DSHS 13-678A (REV. 07/2023)",
            pageLabel: "Page 1 of 2",
            subtitle: "TO BE COMPLETED FOR DELEGATED AS NEEDED MEDICATIONS",
        },
        printFooter: [

        ],
        defaults: {
            clientName: "",
            acesId: "",
            clientDob: "",
            setting: "",
            prnOrders: [createEmptyPrnMedicationOrder()],
            rndSignature: "",
            rndSignatureDate: "",
        },
        sections: [
            {
                title: "Resident and Setting",
                subtitle: "Start with the client information and care setting for this PRN medication delegation record.",
                fields: [
                    { kind: "text", field: "clientName", label: "Client Name", cols: "col-12 md:col-4" },
                    { kind: "text", field: "acesId", label: "ACES ID Number", cols: "col-12 md:col-4" },
                    { kind: "date", field: "clientDob", label: "Date of Birth", cols: "col-12 md:col-4" },
                    { kind: "text", field: "setting", label: "Setting", cols: "col-12 md:col-6" },
                ],
            },
            {
                title: "PRN Medication Orders",
                subtitle: "Add as many PRN medication blocks as needed for this resident's delegated PRN medications.",
                fields: [],
            },
            {
                title: "Nurse Sign-Off",
                subtitle: "Review the PRN instructions, then sign and date the delegation record.",
                fields: [
                    { kind: "text", field: "rndSignature", label: "RND Signature", cols: "col-12 md:col-6" },
                    { kind: "date", field: "rndSignatureDate", label: "Date", cols: "col-12 md:col-6" },
                ],
            },
        ],
    },
    "13-678B": {
        notes: [
            "Setting, facility or program name, and telephone number are optional per the instruction section.",
            "The assumption statement on the official form confirms accountability for delegated tasks and nursing supervision.",
            "The official form includes the Nurse Delegation complaint line and distribution note in the footer.",
        ],
        instructionsTitle: "Instructions for Completing Nurse Delegation: Assumption of Delegation",
        instructions: [
            "All fields are required unless indicated \"OPTIONAL\".",
            "1. Client Name: Enter the ND client's name.",
            "2. ACES ID: Enter the client's ACES identification number.",
            "3. Date of Birth: Enter the ND client's date of birth.",
            "4. Setting: OPTIONAL - Enter the client ID assigned by your business or the setting such as AFH, ALF, DDA program, or In-home.",
            "5. Facility or Program Name: OPTIONAL - Enter the facility or program contact name.",
            "6. Telephone Number: OPTIONAL - Enter the facility or program contact telephone number including area code.",
            "7. Reason / Dates for Another RND to Assume Delegation: Enter why the other RND rescinded and the date you assume delegation responsibility.",
            "The assumption statement on the official form confirms that the assuming RND knows the client through assessment, plan of care, LTCW skills, and delegated tasks, and is accepting accountability for supervision.",
            "8 and 9. Assuming RND Signature and Date: Sign and date the form.",
        ],
        printHint: "Print this as a short assumption-of-delegation handoff record with one concise signature block.",
        printHeader: {
            title: "Nurse Delegation: Assumption of Delegation",
            revision: "DSHS 13-678B (REV. 09/2021)",
            subtitle: "Assuming RND confirms responsibility and accountability for delegated tasks and supervision.",
        },
        printFooter: [

        ],
        defaults: {
            clientName: "",
            acesId: "",
            clientDob: "",
            setting: "",
            facilityProgramName: "",
            telephoneNumber: "",
            assumptionReason: "",
            rndSignature: "",
            rndSignatureDate: "",
        },
        sections: [
            {
                title: "Assumption Details",
                subtitle: "Document the client, setting, and why another delegating nurse is assuming responsibility.",
                fields: [
                    { kind: "text", field: "clientName", label: "Client Name", cols: "col-12 md:col-4" },
                    { kind: "text", field: "acesId", label: "ACES ID", cols: "col-12 md:col-4" },
                    { kind: "date", field: "clientDob", label: "Date of Birth", cols: "col-12 md:col-4" },
                    { kind: "text", field: "setting", label: "Setting", cols: "col-12 md:col-4" },
                    { kind: "text", field: "facilityProgramName", label: "Facility or Program Name", cols: "col-12 md:col-4" },
                    { kind: "text", field: "telephoneNumber", label: "Telephone Number", cols: "col-12 md:col-4" },
                    { kind: "textarea", field: "assumptionReason", label: "Reason / Dates for Another RND to Assume Delegation", cols: "col-12", rows: 4 },
                    { kind: "text", field: "rndSignature", label: "Assuming RND Signature", cols: "col-12 md:col-6" },
                    { kind: "date", field: "rndSignatureDate", label: "Date", cols: "col-12 md:col-6" },
                ],
            },
        ],
    },
    "13-680": {
        notes: [
            "The official footer includes the Nurse Delegation complaint line and distribution note.",
            "The original form includes case manager notification fields and an alternative plan for continuing the task.",
            "The instruction page says all fields are required unless indicated OPTIONAL.",
        ],
        instructionsTitle: "Instructions for Completing Nurse Delegation: Rescinding Delegation",
        instructions: [
            "All fields are required unless indicated \"OPTIONAL\".",
            "1. Client Name: Enter the ND client's name.",
            "2. ACES Client ID Number: Enter the client's ACES ID number.",
            "3. Date of Birth: Enter the ND client's date of birth.",
            "4. Setting: Enter the client setting such as AFH, ALF, DDA Program, or In-home.",
            "5. Facility or Program Name: Enter the facility or program contact name.",
            "6. Telephone Number: Enter the facility or program contact telephone number including area code.",
            "7. Reason for Rescinding: Mark all boxes that apply.",
            "8. Names of Caregivers: Enter the name of each individual caregiver rescinded.",
            "9. Medications and Treatments Rescinded: Enter the name of each medication or treatment rescinded.",
            "10. Notes: List notes related to rescinded tasks.",
            "11 to 13. Case Manager Notification: Enter the case manager name if notified, the notification method, and the date of notice.",
            "14. Alternative Plan for Continuing the Task: Describe how the client's needs will continue to be met.",
            "15 and 16. RND Signature and Date: Sign and date the form. The signature date is the effective date of rescinding.",
        ],
        printHint: "Print with the rescinding reasons visible as a checklist, similar to the original rescinding form.",
        printHeader: {
            bannerLines: [
                "DISTRIBUTION: Copy in client chart and in RND file",
            ],
            title: "Nurse Delegation: Rescinding Delegation",
            revision: "DSHS 13-680 (REV. 09/2021)",
            pageLabel: "Page 1 of 2",
        },
        printFooter: [

        ],
        defaults: {
            clientName: "",
            acesId: "",
            clientDob: "",
            setting: "",
            facilityProgramName: "",
            telephoneNumber: "",
            rescindingReasons: [] as string[],
            otherReason: "",
            rescindedEntries: [createEmptyRescindingDelegationRow()],
            caregiverNames: "",
            rescindedTasks: "",
            notes: "",
            caseManagerName: "",
            caseManagerNotificationMethod: "",
            notificationDate: "",
            alternativePlan: "",
            rndSignature: "",
            rndSignatureDate: "",
        },
        sections: [
            {
                title: "Rescinding Context",
                subtitle: "Identify the resident, program, and the reason delegation is ending.",
                fields: [
                    { kind: "text", field: "clientName", label: "Client Name", cols: "col-12 md:col-4" },
                    { kind: "text", field: "acesId", label: "ACES Client ID Number", cols: "col-12 md:col-4" },
                    { kind: "date", field: "clientDob", label: "Date of Birth", cols: "col-12 md:col-4" },
                    { kind: "text", field: "setting", label: "Setting", cols: "col-12 md:col-4" },
                    { kind: "text", field: "facilityProgramName", label: "Facility or Program Name", cols: "col-12 md:col-4" },
                    { kind: "text", field: "telephoneNumber", label: "Telephone Number", cols: "col-12 md:col-4" },
                    { kind: "checkboxGroup", field: "rescindingReasons", label: "Reason for Rescinding", cols: "col-12", options: ["Client died", "Client condition no longer stable and predictable", "Frequent staff turnover", "Client / authorized representative requested", "NA not competent", "NA not willing", "NA credential expired", "NA no longer working with client", "Client safety compromised", "Rescinding facility including clients and nurse assistant", "Other"] },
                    { kind: "text", field: "otherReason", label: "Other Reason", cols: "col-12" },
                ],
            },
            {
                title: "Caregivers, Notification, and Follow-Up",
                subtitle: "List the caregivers involved in the same caregiver / medication / notes table shown on the official form, then complete the case manager and follow-up details.",
                fields: [
                    { kind: "text", field: "caseManagerName", label: "Name of Case Manager Notified", cols: "col-12 md:col-6" },
                    { kind: "radioGroup", field: "caseManagerNotificationMethod", label: "Method of Notification", cols: "col-12 md:col-6", options: [{ label: "Telephone", value: "telephone" }, { label: "Email", value: "email" }] },
                    { kind: "date", field: "notificationDate", label: "Date", cols: "col-12 md:col-4" },
                    { kind: "textarea", field: "alternativePlan", label: "Alternative Plan for Continuing the Task", cols: "col-12", rows: 4 },
                    { kind: "text", field: "rndSignature", label: "RND Signature", cols: "col-12 md:col-8" },
                    { kind: "date", field: "rndSignatureDate", label: "Date", cols: "col-12 md:col-4" },
                ],
            },
        ],
    },
    "13-681": {
        notes: [
            "The original form includes a complaint line and distribution note in the header/footer.",
            "If the order was verbal, the verifier name, title, and verification date section must be completed.",
            "The official form requires selecting only one delegation decision: delegate immediately or require a site visit first.",
        ],
        instructionsTitle: "Instructions for Completing Nurse Delegation: Change in Medical / Treatment Orders",
        instructions: [
            "All fields are required unless indicated \"OPTIONAL\".",
            "1 to 4. Enter the client name, ACES ID number, date of birth, and setting details shown on the official form.",
            "5. Date RND Was Notified: Enter the date the nurse was notified of the change.",
            "6. By Whom: Enter the name and title of the person who notified the delegating nurse.",
            "7. Change in Orders: Check the appropriate box to show whether the change is a new medication, changed delegated medication, new nursing task, or changed nursing task.",
            "8. How Was the Change Received: Select written, faxed, or verbal.",
            "9. Effective Date of Change: Enter the date the provider ordered the change.",
            "10. Verbal Verification: Complete this only when the order was verbal.",
            "11. Nursing Task / Orders: Describe the order and whether a new task sheet is required or the current sheet was updated.",
            "12 to 21. Medication fields: Complete for each new or changed medication, including date ordered, name, start or stop dates, dose, frequency, route, maximum dosing, and reason for medication.",
            "22 to 30. Optional Task Sheet fields: Use these when the form is also serving as a task sheet, including steps to perform the task, expected outcome, and reporting instructions.",
            "31 or 32. Delegation Decision: Choose only one, either delegate immediately or require a site visit first.",
            "33 and 34. RND Signature and Date: Sign and date your signature.",
        ],
        printHint: "Keep the order-change details, medication details, and reporting instructions grouped in print as three clear blocks.",
        printHeader: {
            bannerLines: [
                "DISTRIBUTION: Copy in client chart and in RND file",
            ],
            title: "Nurse Delegation: Change in Medical / Treatment Orders",
            revision: "DSHS 13-681 (REV. 02/2022)",
            pageLabel: "Page 1 of 2",
        },
        printFooter: [

        ],
        defaults: {
            clientName: "",
            acesId: "",
            clientDob: "",
            setting: "",
            dateNotified: "",
            notifiedBy: "",
            changeTypes: [] as string[],
            changeReceivedMethod: "written",
            effectiveDate: "",
            verbalVerificationName: "",
            verbalVerificationTitle: "",
            verbalVerificationDate: "",
            nursingTaskOrder: "",
            taskSheetStatus: [] as string[],
            medicationChangeType: [] as string[],
            medicationDateOrdered: "",
            medicationName: "",
            medicationStartDate: "",
            medicationStopDate: "",
            teachingAidAttached: [] as string[],
            medicationDose: "",
            medicationFrequency: "",
            medicationRoute: "",
            medicationNotToExceed: "",
            medicationReason: "",
            stepsToPerformTask: "",
            expectedOutcome: "",
            rndName: "",
            rndPhone: "",
            reportToRnd: "",
            healthCareProvider: "",
            healthCareProviderPhone: "",
            reportToHealthCareProvider: "",
            reportToEmergencyServices: "",
            delegationDecision: [] as string[],
            rndSignature: "",
            rndSignatureDate: "",
        },
        sections: [
            {
                title: "Order Change Details",
                subtitle: "Capture who notified the nurse, the type of order change, and how the change was communicated.",
                fields: [
                    { kind: "text", field: "clientName", label: "Client Name", cols: "col-12 md:col-4" },
                    { kind: "text", field: "acesId", label: "ACES ID Number", cols: "col-12 md:col-4" },
                    { kind: "date", field: "clientDob", label: "Date of Birth", cols: "col-12 md:col-4" },
                    { kind: "text", field: "setting", label: "ID / Setting (Optional)", cols: "col-12 md:col-4" },
                    { kind: "date", field: "dateNotified", label: "Date RND Was Notified", cols: "col-12 md:col-4" },
                    { kind: "text", field: "notifiedBy", label: "By Whom", cols: "col-12 md:col-4" },
                    { kind: "checkboxGroup", field: "changeTypes", label: "Changes in Order(s)", cols: "col-12", options: ["New med", "Change in a delegated med", "New nursing task", "Change in a nursing task"] },
                    { kind: "radioGroup", field: "changeReceivedMethod", label: "How Was the Change Received?", cols: "col-12 md:col-8", options: [{ label: "Written", value: "written" }, { label: "Faxed", value: "faxed" }, { label: "Verbal", value: "verbal" }] },
                    { kind: "date", field: "effectiveDate", label: "Effective Date of Change", cols: "col-12 md:col-4" },
                    { kind: "text", field: "verbalVerificationName", label: "Verifying Person Name", cols: "col-12 md:col-4" },
                    { kind: "text", field: "verbalVerificationTitle", label: "Verifying Person Title", cols: "col-12 md:col-4" },
                    { kind: "date", field: "verbalVerificationDate", label: "Date of Verification", cols: "col-12 md:col-4" },
                ],
            },
            {
                title: "Task and Medication Updates",
                subtitle: "Describe the nursing task, medication change, and any updated task sheet information.",
                fields: [
                    { kind: "checkboxGroup", field: "taskSheetStatus", label: "Nursing Task(s)", cols: "col-12 md:col-8", options: ["New task sheet required", "Current task sheet updated", "No change to task sheet"] },
                    { kind: "textarea", field: "nursingTaskOrder", label: "Nursing Task / Order", cols: "col-12", rows: 3 },
                    { kind: "checkboxGroup", field: "medicationChangeType", label: "Medication Status", cols: "col-12 md:col-4", options: ["New", "Changed"] },
                    { kind: "date", field: "medicationDateOrdered", label: "Date Ordered", cols: "col-12 md:col-4" },
                    { kind: "text", field: "medicationName", label: "Medication Name", cols: "col-12 md:col-4" },
                    { kind: "date", field: "medicationStartDate", label: "Start Date", cols: "col-12 md:col-4" },
                    { kind: "date", field: "medicationStopDate", label: "Stop Date", cols: "col-12 md:col-4" },
                    { kind: "text", field: "medicationDose", label: "Strength / Dose", cols: "col-12 md:col-4" },
                    { kind: "text", field: "medicationFrequency", label: "Medication Frequency", cols: "col-12 md:col-6" },
                    { kind: "text", field: "medicationRoute", label: "Route", cols: "col-12 md:col-6" },
                    { kind: "text", field: "medicationNotToExceed", label: "Not to Exceed", cols: "col-12 md:col-6" },
                    { kind: "text", field: "medicationReason", label: "Reason for Medication", cols: "col-12 md:col-6" },
                    { kind: "textarea", field: "stepsToPerformTask", label: "Steps to Perform the New Task", cols: "col-12 md:col-6", rows: 4 },
                    { kind: "checkboxGroup", field: "teachingAidAttached", label: "Teaching Aid Attached", cols: "col-12 md:col-6", options: ["Check if teaching aid attached"] },
                    { kind: "textarea", field: "expectedOutcome", label: "Expected Outcome of Delegated Task", cols: "col-12 md:col-6", rows: 4 },
                ],
            },
            {
                title: "Reporting and Delegation Decision",
                subtitle: "Document reporting instructions, who to contact, and whether delegation may proceed immediately.",
                fields: [
                    { kind: "text", field: "rndName", label: "RND Name", cols: "col-12 md:col-6" },
                    { kind: "text", field: "rndPhone", label: "RND Telephone Number", cols: "col-12 md:col-6" },
                    { kind: "text", field: "healthCareProvider", label: "Health Care Provider", cols: "col-12 md:col-6" },
                    { kind: "text", field: "healthCareProviderPhone", label: "Health Care Provider Phone", cols: "col-12 md:col-6" },
                    { kind: "textarea", field: "reportToRnd", label: "What to Report to RND", cols: "col-12 md:col-4", rows: 4 },
                    { kind: "textarea", field: "reportToHealthCareProvider", label: "What to Report to Health Care Provider", cols: "col-12 md:col-4", rows: 4 },
                    { kind: "textarea", field: "reportToEmergencyServices", label: "What to Report to Emergency Services, 911", cols: "col-12 md:col-4", rows: 4 },
                    { kind: "checkboxGroup", field: "delegationDecision", label: "Delegation Decision", cols: "col-12 md:col-8", options: ["Delegate immediately. No site visit required.", "A site visit is required for training or assessment prior to delegation."] },
                    { kind: "text", field: "rndSignature", label: "RND Signature", cols: "col-12 md:col-8" },
                    { kind: "date", field: "rndSignatureDate", label: "Date", cols: "col-12 md:col-4" },
                ],
            },
        ],
    },
    "13-893": {
        templateVersion: "13-893@2024-11",
        schemaVersion: 1,
        notes: [
            "The official sheet is labeled Aging and Long-Term Support Administration.",
            "All attachments must include the client name, date, and which form they are attached to.",
            "The official footer instructs users to scan and email the request to nursedelegation@dshs.wa.gov.",
        ],
        instructionsTitle: "Request For Additional Units Guidance",
        instructions: [
            "The official form is completed by the delegating nurse.",
            "Enter the RND contact information, client information, and case manager details.",
            "Document the month already authorized and the total number of units needed for that same month.",
            "State the reason additional units are requested and list the supporting documents included with the request.",
            "All attachments must include the client name, date, and which form the attachment supports.",
            "After completion, scan and email the request to nursedelegation@dshs.wa.gov per the official form footer.",
        ],
        printHint: "Keep the request, supporting documents, and approval signature together on print so the form reads like the DSHS request sheet.",
        printHeader: {
            title: "Nurse Delegation: Request For Additional Units",
            revision: "DSHS 13-893 (REV. 11/2024)",
            subtitle: "AGING AND LONG-TERM SUPPORT ADMINISTRATION",
        },
        printFooter: [

        ],
        defaults: {
            rndName: "",
            rndPhone: "",
            rndEmail: "",
            clientName: "",
            acesId: "",
            clientDob: "",
            caseManagerName: "",
            caseManagerPhone: "",
            caseManagerEmail: "",
            additionalUnitsNeeded: "",
            requestMonth: "",
            totalUnitsForMonth: "",
            requestComments: "",
            supportingDocuments: [] as string[],
            requestDate: "",
            requestingSignature: "",
            unitsApproved: "",
            managerSignature: "",
            approvalDate: "",
        },
        sections: [
            {
                title: "Request Details",
                subtitle: "Capture the delegating nurse, client, and case manager details tied to the additional unit request.",
                fields: [
                    { kind: "text", field: "rndName", label: "RND Name", cols: "col-12 md:col-4" },
                    { kind: "text", field: "rndPhone", label: "RND Telephone Number", cols: "col-12 md:col-4" },
                    { kind: "text", field: "rndEmail", label: "RND Email Address", cols: "col-12 md:col-4" },
                    { kind: "text", field: "clientName", label: "Client's Name", cols: "col-12 md:col-4" },
                    { kind: "text", field: "acesId", label: "ACES ID Number", cols: "col-12 md:col-4" },
                    { kind: "date", field: "clientDob", label: "Client's Date of Birth", cols: "col-12 md:col-4" },
                    { kind: "text", field: "caseManagerName", label: "Case Manager's Name", cols: "col-12 md:col-4" },
                    { kind: "text", field: "caseManagerPhone", label: "Case Manager's Telephone Number", cols: "col-12 md:col-4" },
                    { kind: "text", field: "caseManagerEmail", label: "Case Manager's E-Mail", cols: "col-12 md:col-4" },
                ],
            },
            {
                title: "Units Requested",
                subtitle: "Explain the month being requested, how many units are needed, and the supporting documents included.",
                fields: [
                    { kind: "text", field: "additionalUnitsNeeded", label: "Additional Units Needed", cols: "col-12 md:col-4" },
                    { kind: "text", field: "requestMonth", label: "Month of Request", cols: "col-12 md:col-4" },
                    { kind: "text", field: "totalUnitsForMonth", label: "Total Units for the Month", cols: "col-12 md:col-4" },
                    { kind: "textarea", field: "requestComments", label: "Reason Additional Units Requested / Comments", cols: "col-12", rows: 4 },
                    { kind: "checkboxGroup", field: "supportingDocuments", label: "Supporting Documents Included", cols: "col-12", options: ["14-484 Nurse Delegation: Nursing Visit", "13-678, Nurse Delegation: Instructions for Nursing Task (page 2)", "10-217 Nurse Delegation: Credentials and Training Verification for new delegated caregivers", "Billing Tracker for the month of request", "All other supporting documents to support time."] },
                ],
            },
            {
                title: "Request and Approval",
                subtitle: "Track the nurse request signature and the approval details completed by the ND program manager.",
                fields: [
                    { kind: "date", field: "requestDate", label: "Date Requested", cols: "col-12 md:col-4" },
                    { kind: "text", field: "requestingSignature", label: "Requesting ND Signature", cols: "col-12 md:col-8" },
                    { kind: "text", field: "unitsApproved", label: "Units Approved", cols: "col-12 md:col-4" },
                    { kind: "text", field: "managerSignature", label: "ND Program Manager Signature", cols: "col-12 md:col-4" },
                    { kind: "date", field: "approvalDate", label: "Date Approved", cols: "col-12 md:col-4" },
                ],
            },
        ],
    },
    "14-484": {
        templateVersion: "14-484@2023-07",
        schemaVersion: 1,
        notes: [
            "The official form includes the Nurse Delegation complaint line and distribution note on each page.",
            "Page 3 of the official form is an instructions page.",
            "The instruction page says the client assessment must be completed and attached when using the nursing visit form.",
        ],
        instructionsTitle: "Instructions for Completing Nurse Delegation: Nursing Visit",
        instructions: [
            "All fields are required unless marked \"OPTIONAL\".",
            "1 to 4. Enter the client name, ACES ID number, date of birth, and care setting.",
            "5. Check all boxes that apply for how the form is being used. Assessment must be completed and attached.",
            "6. Client Requires Nurse Delegation For These Delegated Task(s): List the delegated tasks and why the client needs them.",
            "7. Review Of Systems: Check \"No change\" if unchanged from the last assessment, or check the appropriate changed categories and describe them in notes.",
            "8. Notes: Describe changes in condition and attach assessment documentation when condition changes are marked. This area may also be used as a progress note section.",
            "9. LTCW Training / Competency: Record each LTCW evaluated and mark the competency methods and training status used. The official instructions describe columns A through G, including observation, verbal description, record review, training needed or completed, other notes, and active credential verification.",
            "10. OPTIONAL: Mark this when a second page is used for additional notes or LTCW names.",
            "11. Delegation Status: Check all boxes that apply. If rescinding delegation is checked, the rescinding form must also be completed.",
            "12 to 14. Sign, date, and document the return-visit due date on page 1.",
            "15 to 22. If a second page is used, repeat the identifying information, notes, competency entries, and signature / return-visit fields exactly as directed on the official page 3 instructions.",
            "Be sure to sign and date both pages if a second page is used.",
        ],
        printHint: "Print in a nursing-visit worksheet format so the visit overview, review of systems, and competency sections break cleanly like the original.",
        printHeader: {
            bannerLines: [
                "DISTRIBUTION: Copy in client chart and in RND file",
            ],
            title: "Nurse Delegation: Nursing Visit",
            revision: "DSHS 14-484 (REV. 07/2023)",
            pageLabel: "Page 1 of 3",
        },
        printFooter: [

        ],
        defaults: {
            clientName: "",
            acesId: "",
            clientDob: "",
            setting: [] as string[],
            settingOther: "",
            visitTypes: [] as string[],
            visitTypeOther: "",
            delegatedTasks: "",
            relatedTo: "",
            reviewOfSystems: [] as string[],
            notes: "",
            ltcwCompetencyEntries: [createEmptyLtcwCompetencyEntry()],
            additionalPageUsed: false,
            delegationStatus: [] as string[],
            rndSignature: "",
            rndSignatureDate: "",
            returnVisitDate: "",
        },
        sections: [
            {
                title: "Visit Overview",
                subtitle: "Capture the resident, care setting, visit purpose, and the delegated tasks reviewed during the nursing visit.",
                fields: [
                    { kind: "text", field: "clientName", label: "Client Name", cols: "col-12 md:col-4" },
                    { kind: "text", field: "acesId", label: "ACES ID Number", cols: "col-12 md:col-4" },
                    { kind: "date", field: "clientDob", label: "Date of Birth", cols: "col-12 md:col-4" },
                    { kind: "checkboxGroup", field: "setting", label: "Setting", cols: "col-12", options: ["AFH", "DDA", "In-home", "Other"] },
                    { kind: "text", field: "settingOther", label: "Other Setting", cols: "col-12 md:col-6" },
                    { kind: "checkboxGroup", field: "visitTypes", label: "Check All Visit Types That Apply", cols: "col-12", options: ["Client Assessment", "Supervisory Visit", "Initial Caregiver Delegation", "Condition Change", "Initial Insulin Delegation", "Other"] },
                    { kind: "text", field: "visitTypeOther", label: "Other Visit Type", cols: "col-12 md:col-6" },
                    { kind: "textarea", field: "delegatedTasks", label: "Client Requires Nurse Delegation for These Tasks", cols: "col-12 md:col-6", rows: 4 },
                    { kind: "textarea", field: "relatedTo", label: "Related To", cols: "col-12 md:col-6", rows: 4 },
                ],
            },
            {
                title: "Condition Review",
                subtitle: "Document changes in condition, review-of-systems findings, and the visit notes for this resident.",
                fields: [
                    { kind: "checkboxGroup", field: "reviewOfSystems", label: "Review of Systems: Only check changes in condition from last assessment", cols: "col-12", options: ["No Change", "Cardiovascular", "Diet / Weight / Nutrition", "Neurological", "GU / Reproductive", "GI", "Respiratory", "Endocrine", "ADL", "Sensory", "Pain", "Integumentary", "Psych / Social", "Musculoskeletal", "Cognition", "Other"] },
                    { kind: "textarea", field: "notes", label: "Visit Notes", cols: "col-12", rows: 5 },
                ],
            },
            {
                title: "Caregiver Competency and Follow-Up",
                subtitle: "Track each LTCW competency row, mark the applicable training methods, and complete the required sign-off details.",
                fields: [
                    { kind: "checkbox", field: "additionalPageUsed", label: "Check here if additional notes / LTCW name continue on page 2", cols: "col-12 md:col-4" },
                    { kind: "checkboxGroup", field: "delegationStatus", label: "Delegation Status", cols: "col-12", options: ["Client stable and predictable", "Continue delegation", "See rescind form"] },
                    { kind: "text", field: "rndSignature", label: "RND Signature", cols: "col-12 md:col-8" },
                    { kind: "date", field: "rndSignatureDate", label: "Date", cols: "col-12 md:col-2" },
                    { kind: "date", field: "returnVisitDate", label: "Return Visit On or Before", cols: "col-12 md:col-3" },
                ],
            },
        ],
    },
};

export const FALLBACK_STANDARD_DELEGATION_DEFAULTS = {
    residentName: "",
    formTitle: "",
    requestedBy: "",
    effectiveDate: "",
    dueDate: "",
    providerName: "",
    providerPhone: "",
    delegatingNurse: "",
    serviceLocation: "",
    taskSummary: "",
    medicationOrProcedure: "",
    careInstructions: "",
    monitoringRequirements: "",
    changeTriggers: "",
    followUpPlan: "",
    notes: "",
    signatureName: "",
    signatureDate: "",
};

const STANDARD_DELEGATION_TEMPLATE_VERSION_INDEX = Object.entries(STANDARD_DELEGATION_FORM_DEFINITIONS)
    .reduce((acc, [formCode, definition]) => {
        const resolvedTemplateVersion = String(definition?.templateVersion || `${formCode}@current`).trim();
        acc[resolvedTemplateVersion] = {
            ...definition,
            templateVersion: resolvedTemplateVersion,
            schemaVersion: Number(definition?.schemaVersion || 1),
        };
        return acc;
    }, {} as Record<string, StandardDelegationTemplateSnapshot>);

export const getLatestDelegationTemplateVersion = (formCode: string) =>
    String(STANDARD_DELEGATION_FORM_DEFINITIONS[formCode]?.templateVersion || `${String(formCode || "").trim().toUpperCase()}@current`).trim();

export const getDelegationTemplateDefinition = (
    formCode: string,
    templateVersion?: string | null,
    templateSnapshot?: StandardDelegationFormDefinition | null,
): StandardDelegationTemplateSnapshot | null => {
    if (templateSnapshot && Array.isArray(templateSnapshot.sections)) {
        const resolvedTemplateVersion = String(
            templateSnapshot.templateVersion
            || templateVersion
            || getLatestDelegationTemplateVersion(formCode)
        ).trim();
        return {
            ...templateSnapshot,
            templateVersion: resolvedTemplateVersion,
            schemaVersion: Number(templateSnapshot.schemaVersion || 1),
        };
    }

    const resolvedTemplateVersion = String(templateVersion || "").trim();
    if (resolvedTemplateVersion && STANDARD_DELEGATION_TEMPLATE_VERSION_INDEX[resolvedTemplateVersion]) {
        return STANDARD_DELEGATION_TEMPLATE_VERSION_INDEX[resolvedTemplateVersion];
    }

    const latestDefinition = STANDARD_DELEGATION_FORM_DEFINITIONS[formCode];
    if (!latestDefinition) {
        return null;
    }

    return {
        ...latestDefinition,
        templateVersion: getLatestDelegationTemplateVersion(formCode),
        schemaVersion: Number(latestDefinition.schemaVersion || 1),
    };
};

export const getDelegationFormNotes = (formCode: string) => {
    const definition = getDelegationTemplateDefinition(formCode);
    return definition?.notes || [];
};

export const getDelegationPrintHint = (formCode: string) => {
    const definition = getDelegationTemplateDefinition(formCode);
    return definition?.printHint || "";
};

export const getDelegationInstructionsTitle = (formCode: string, templateVersion?: string | null, templateSnapshot?: StandardDelegationFormDefinition | null) => {
    const definition = getDelegationTemplateDefinition(formCode, templateVersion, templateSnapshot);
    return definition?.instructionsTitle || "";
};

export const getDelegationInstructions = (formCode: string, templateVersion?: string | null, templateSnapshot?: StandardDelegationFormDefinition | null) => {
    const definition = getDelegationTemplateDefinition(formCode, templateVersion, templateSnapshot);
    return definition?.instructions || [];
};

export const getDelegationPrintHeader = (formCode: string, templateVersion?: string | null, templateSnapshot?: StandardDelegationFormDefinition | null) => {
    const definition = getDelegationTemplateDefinition(formCode, templateVersion, templateSnapshot);
    return definition?.printHeader || null;
};

export const getDelegationPrintFooter = (formCode: string, templateVersion?: string | null, templateSnapshot?: StandardDelegationFormDefinition | null) => {
    const definition = getDelegationTemplateDefinition(formCode, templateVersion, templateSnapshot);
    return definition?.printFooter || [];
};

export const createDefaultStandardDelegationForm = (
    residentName = "",
    formTitle = "",
    formCode = "",
    templateVersion?: string | null,
    templateSnapshot?: StandardDelegationFormDefinition | null,
) => {
    const definition = getDelegationTemplateDefinition(formCode, templateVersion, templateSnapshot);
    const defaults = JSON.parse(JSON.stringify(definition?.defaults || FALLBACK_STANDARD_DELEGATION_DEFAULTS));

    if ("residentName" in defaults) {
        defaults.residentName = residentName;
    }
    if ("clientName" in defaults) {
        defaults.clientName = residentName;
    }

    return {
        ...defaults,
        formTitle,
    };
};

